import { CompleteBooking } from "../types/Booking";
import { BookingType } from "../enums/BookingScope";
import { FacilityType } from "../enums/FacilityType";
import { getSportName } from "./stringUtil";

/**
 * Condivisione di una partita: stesso link e stesso testo dello ShareButton
 * dell'app mobile, così chi riceve il messaggio atterra sulla pagina pubblica
 * `/Share/:id` (che apre l'app se installata).
 */

const PUBLIC_SITE_URL = "https://getmoveapp.com";

const SPORT_EMOJI: Record<number, string> = {
  1: "🎾",
  2: "🎾",
  3: "⚽",
  4: "🏀",
  5: "🏐",
  6: "🏸",
  7: "🏓",
  8: "🏐",
  9: "🏓",
};

export const getSportEmoji = (sportId: number): string =>
  SPORT_EMOJI[sportId] || "🏆";

export const getBookingShareUrl = (booking: Pick<CompleteBooking, "id">) =>
  `${PUBLIC_SITE_URL}/Share/${booking.id}`;

const bookingName = (booking: CompleteBooking): string => {
  const sport = booking.sport?.name ? getSportName(booking.sport.name) : "";
  if (booking.customType) return `${booking.customType}${sport ? ` di ${sport}` : ""}`;
  switch (booking.bookingType) {
    case BookingType.Tournament:
      return `Torneo di ${sport}`;
    case BookingType.Training:
      return `Lezione di ${sport}`;
    default:
      return `Partita di ${sport}`;
  }
};

export const buildBookingShareMessage = (booking: CompleteBooking): string => {
  const facility = booking.facility;
  const where = [
    facility?.structure?.name,
    facility?.name
      ? `${facility.name} (${
          facility.type === FacilityType.Indoor ? "Indoor" : "Outdoor"
        })`
      : "",
  ]
    .filter(Boolean)
    .join(" - ");
  const when = `${new Date(booking.date).toLocaleDateString("it-IT")} - ${
    booking.time
  }`;

  return `La tua partita ti aspetta! ${getSportEmoji(
    booking.sportId
  )} ${bookingName(booking)} - 🏟️ ${where} - 📅 ${when}`;
};

/** Testo completo (messaggio + link) da incollare o inviare. */
export const buildBookingShareText = (booking: CompleteBooking): string =>
  `${buildBookingShareMessage(booking)}\n${getBookingShareUrl(booking)}`;

/** Link universale di WhatsApp: apre l'app o WhatsApp Web con il testo precompilato. */
export const getWhatsAppShareUrl = (text: string): string =>
  `https://wa.me/?text=${encodeURIComponent(text)}`;

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback sotto */
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};
